import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { Chip } from 'src/components/shared/Chip'
import { SectionCard } from 'src/components/shared/SectionCard'
import { UNDERWRITING_STATUS_LABELS } from 'src/constants/oatfi-business/constants'
import { UnderwritingStatus } from 'src/constants/oatfi-business/enums'
import { semanticColors } from 'src/constants/semanticTokens'
import type { OatfiBusiness } from 'src/interfaces/oatfiBusiness'

interface Props {
  business: OatfiBusiness | null
  // `oatfi_manage`. Refresh is a GET and stays for everyone who can open the tab.
  canRequest: boolean
  isRequesting: boolean
  isRefreshing: boolean
  onRequest(): void
  onRefresh(): void
}

/**
 * How each status reads. `APPROVED` gets the success presentation and `ERROR` gets the error
 * one — getting this pairing right (Retool previously had the two swapped, so a declined
 * account could read as approved) is the whole point of this card.
 */
const STATUS_PRESENTATION: Record<
  UnderwritingStatus,
  { background: string; foreground: string; description: string }
> = {
  [UnderwritingStatus.Processing]: {
    background: semanticColors.feedback.infoBg,
    foreground: semanticColors.feedback.infoFg,
    description: 'The lender has the application and has not decided yet.',
  },
  [UnderwritingStatus.ManualReview]: {
    background: semanticColors.feedback.warningBg,
    foreground: semanticColors.feedback.warningFg,
    description: 'A person at the lender is reviewing this application.',
  },
  [UnderwritingStatus.Approved]: {
    background: semanticColors.feedback.successBg,
    foreground: semanticColors.feedback.successFg,
    description: 'Approved for financing.',
  },
  [UnderwritingStatus.Declined]: {
    background: semanticColors.feedback.errorBg,
    foreground: semanticColors.feedback.errorFg,
    description: 'The lender declined this application.',
  },
  [UnderwritingStatus.Error]: {
    background: semanticColors.feedback.errorBg,
    foreground: semanticColors.feedback.errorFg,
    description:
      'The lender returned an error. The reason is only in the server logs — the API does not pass it through.',
  },
}

const NOT_REQUESTED_DESCRIPTION =
  'Underwriting has not been requested for this customer yet. It can be requested as soon as the business profile is saved.'

const UnderwritingStatusCardComponent = ({
  business,
  canRequest,
  isRequesting,
  isRefreshing,
  onRequest,
  onRefresh,
}: Props) => {
  const status = business?.underwritingStatus ?? null
  const presentation = status === null ? null : STATUS_PRESENTATION[status]

  const statusChip =
    status === null || presentation === null ? (
      <Chip
        text="Not requested"
        className={tw`bg-shades-100`}
        textClassName={tw`text-shades-500`}
        dataTestId="underwriting-status-chip"
      />
    ) : (
      <Chip
        text={UNDERWRITING_STATUS_LABELS[status]}
        className={{ backgroundColor: presentation.background }}
        textClassName={{ color: presentation.foreground }}
        dataTestId="underwriting-status-chip"
      />
    )

  return (
    <SectionCard
      title="Underwriting"
      actions={statusChip}
      dataTestId="admin-customer-underwriting-status-card"
    >
      <Typography variant="body2" css={tw`text-shades-500`}>
        {presentation === null ? NOT_REQUESTED_DESCRIPTION : presentation.description}
      </Typography>
      {/* The hint is about requesting, so it shows only to someone who can request. */}
      {business === null && canRequest ? (
        <Typography variant="body2" css={tw`text-shades-400`}>
          Save the business profile first.
        </Typography>
      ) : (
        <Fragment />
      )}
      <div css={tw`flex flex-row items-center justify-end gap-2`}>
        {/* The status is written by an Oatfi webhook (handleUnderwriteUpdatedWebhook), so it
            changes with no admin action and no request to correlate it with — PROCESSING can
            become APPROVED while this page sits open. This refetches the business so the card
            can catch up. Polling is deliberately not used: the change is rare. */}
        <Button
          size="small"
          color="white"
          onClick={onRefresh}
          disabled={isRefreshing}
          data-testid="underwriting-refresh-button"
        >
          Refresh
        </Button>
        {/* Disabled only while a business does not exist or a request is in flight — never by
            status. The endpoint has no status precondition, and re-requesting after ERROR or
            after uploading a document is the normal recovery path. */}
        {canRequest ? (
          <Button
            size="small"
            color="dark"
            className={tw`px-4`}
            onClick={onRequest}
            disabled={business === null || isRequesting}
            data-testid="underwriting-request-button"
          >
            Request underwriting
          </Button>
        ) : null}
      </div>
    </SectionCard>
  )
}

export const UnderwritingStatusCard = memo(UnderwritingStatusCardComponent)
